// @flow
import React, { Component, Suspense } from 'react';
import AppStore from 'stores/AppStore';
import CurrentUserAction from 'actions/CurrentUserActions';
import Routes from 'routes/Routes';
import CurrentUser from 'models/CurrentUser';
import Header from './Header';
import Footer from './Footer';
import styled, { injectGlobal } from 'styled-components';
import HomieContext from 'HomieContext';

import ExternalTenantContainer from 'modules/external_tenant/ExternalTenantContainer';
import LoadingProcessHomie from 'components/shared/LoginLogoComponent';
import LoaderPopup from 'utils/LoaderPopup';
import { Switch, Route, BrowserRouter as Router } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { media } from 'styles/Mixins';
import { IntlProvider, addLocaleData } from 'react-intl';
import locale_en from 'react-intl/locale-data/en';
import locale_es from 'react-intl/locale-data/es';
import texts_es from '../../translations/es.json';
import texts_en from '../../translations/en.json';
import { Provider } from 'react-redux';
import reduxStore from 'redux/store';
import ApplicationsDashboard from 'modules/dashboards/applications/ApplicationsDashboard';
import PermissionDashboard from 'modules/dashboards/permissions/PermissionDashboard';
import PermissionsProvider from 'auth/PermissionsProvider';

import { AuthGate, Admin } from 'auth';
import ApiServiceH360 from 'modules/h360/api/ApiService';
import _ from 'lodash';
import ReactGA from 'react-ga4';

const ApplicationReportView = React.lazy(() =>
  import('modules/dashboards/applications/views/ApplicationReportView')
);
const ContainerH360 = React.lazy(() => import('modules/h360/ContainerH360'));
const ContainerAdvisor = React.lazy(() =>
  import('modules/h360/ContainerAdvisor')
);

const CustomerSuccessContainer = React.lazy(() =>
  import('modules/customer_success_dashboard/Container')
);

const HomieLiteDashboard = React.lazy(() =>
  import('views/dashboard/homie_lite/HomieLiteDashboard')
);
const PageError = React.lazy(() => import('react/modules/navent/PageError'));

const AdelantoHomie = React.lazy(() =>
  import('react/modules/adelantohomie/FinancingLayout')
);

const meta = {
  title: 'Homie | La nueva forma de rentar inmuebles, sin aval',
  description:
    'Renta tu inmueble sin necesidad de aval en las mejores colonias de la CDMX.',
  keywords:
    'rentar, depa, departamento, edificio, casa, sin, aval, cdmx, df, colonia, roma, polanco, condesa, napoles, ' +
    'valle, cuauhtemoc, anzures, homie, renta, forma'
};

injectGlobal`
  html, body {
    width:100% !important;
    min-height:100% !important;
  }
  #homie {
    width:100% !important;
    min-height:100% !important;
  }
`;

const Wrapper = styled.div`
  min-height: 100% !important;
`;

const Container = styled.div`
  width: 100%;
  filter: ${props => (props.isBlur ? 'blur(1px)' : 'none')};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  ${media.md`
      max-width: ${props => (props.isDashboard ? '1024px' : '1440px')};
      height: auto !important;
      margin:0 auto;
   `}
`;

const BundleLoadingPlaceholder = styled.div`
  width: 100%;
  height: calc(100vh - 60px);
`;

type StateType = {
  currentUser: CurrentUser | typeof undefined,
  showNavegationsBar: boolean,
  loadingVisible: boolean,
  loadingProcessVisible: boolean,
  loadingProcessText: string,
  language: string,
  agency: any
};

const texts = {
  es: texts_es,
  en: texts_en
};

const H360_AGENCY = {
  id: 'h360',
  slug: 'h360'
};
export default class App extends Component<{}, StateType> {
  state = {
    currentUser: undefined,
    showNavegationsBar: true,
    loadingVisible: false,
    loadingProcessVisible: false,
    loadingProcessText: 'Cargando',
    language: this.initialLanguage,
    agency: {}
  };

  get initialLanguage() {
    addLocaleData([...locale_en, ...locale_es]);
    const lang = localStorage.getItem('myLanguage') || 'es';
    if (
      localStorage.getItem('myLanguage') === null ||
      localStorage.getItem('myLanguage') === undefined
    ) {
      localStorage.setItem('myLanguage', lang);
    }
    return lang;
  }

  onChange = (): void => {
    this.setState(
      {
        currentUser: AppStore.getCurrentUser()
      },
      () => {
        this.getAgency();
      }
    );
  };

  getAgency = () => {
    const { currentUser } = this.state;
    if (currentUser.isAdvisor)
      ApiServiceH360.getUser(currentUser.id).then(response => {
        const agency =
          response.data.user.advisor.agencyBusinessPartner || H360_AGENCY;
        this.setState({
          agency
        });
      });
  };

  componentDidMount() {
    ReactGA.initialize('G-W98SG5E8FN');
    CurrentUserAction.startCurrentUserPolling();
  }

  componentWillMount() {
    AppStore.addPollingChangeListener(this.onChange);
  }

  componentWillUnmount() {
    AppStore.removePollingChangeListener(this.onChange);
  }

  changeLanguage = (lang: string) => {
    localStorage.setItem('myLanguage', lang);
    this.setState({
      language: lang
    });
  };

  hideLoadingProcessView = () => {
    this.setState(
      {
        loadingProcessVisible: false
      },
      () => LoaderPopup.hide()
    );
  };

  showLoadingProcessView = (text: string = 'Cargando') => {
    if (!this.state.loadingProcessVisible) {
      LoaderPopup.show();
      this.setState({
        loadingProcessVisible: true,
        loadingProcessText: text
      });
    }
  };

  hideNavegationBar = () => this.setState({ showNavegationsBar: false });

  showNavegationBar = () => this.setState({ showNavegationsBar: true });

  renderMainRoutes = (isDashboard: boolean = true, isAdminDashboard) => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView,
      languageSelected: this.state.language,
      changeLanguage: this.changeLanguage,
      hideNavegationBar: this.hideNavegationBar,
      showNavegationBar: this.showNavegationBar,
      agency: this.state.agency
    };
    const { agency } = this.state;
    return (
      <HomieContext.Provider value={homieContext}>
        {isDashboard && (
          <Header agency={agency} showNav={this.state.showNavegationsBar} />
        )}
        <Container
          isDashboard={isDashboard}
          isVisible={!this.state.loadingVisible}
          isBlur={this.state.loadingProcessVisible}
          agency={agency}
        >
          <Suspense fallback={<BundleLoadingPlaceholder />}>
            <Routes agency={agency} />
          </Suspense>
          {_.isEmpty(agency) && isDashboard && (
            <Footer showNav={this.state.showNavegationsBar} />
          )}
        </Container>
      </HomieContext.Provider>
    );
  };

  renderAdelantoHomie = () => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <Container
          isVisible={!this.state.loadingVisible}
          isBlur={this.state.loadingProcessVisible}
        >
          <Suspense fallback={<BundleLoadingPlaceholder />}>
            <AdelantoHomie />
          </Suspense>
        </Container>
      </HomieContext.Provider>
    );
  };

  renderExternalTenantRoutes = props => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView,
      languageSelected: this.state.language,
      changeLanguage: this.changeLanguage,
      hideNavegationBar: this.hideNavegationBar,
      showNavegationBar: this.showNavegationBar
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <Suspense fallback={<BundleLoadingPlaceholder />}>
          <ExternalTenantContainer history={props.history} />
        </Suspense>
      </HomieContext.Provider>
    );
  };

  renderCustomerSuccessRoutes = () => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };

    return (
      <HomieContext.Provider value={homieContext}>
        <Suspense fallback={<BundleLoadingPlaceholder />}>
          <CustomerSuccessContainer />
        </Suspense>
      </HomieContext.Provider>
    );
  };

  renderApplicationsDashboardRoutes = () => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };

    return (
      <HomieContext.Provider value={homieContext}>
        <PermissionsProvider>
          <Suspense fallback={BundleLoadingPlaceholder}>
            <ApplicationsDashboard />
          </Suspense>
        </PermissionsProvider>
      </HomieContext.Provider>
    );
  };

  renderPermissionDashboardRoutes = () => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };

    return (
      <HomieContext.Provider value={homieContext}>
        <Suspense fallback={<BundleLoadingPlaceholder />}>
          <PermissionDashboard />
        </Suspense>
      </HomieContext.Provider>
    );
  };

  renderReportDashboardRoutes = props => {
    const homieContext = {
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <PermissionsProvider>
          <AuthGate allowed={[Admin]}>
            <Suspense fallback={<BundleLoadingPlaceholder />}>
              <ApplicationReportView {...props} />
            </Suspense>
          </AuthGate>
        </PermissionsProvider>
      </HomieContext.Provider>
    );
  };

  renderH360Register = props => {
    const homieContext = {
      history: props.history,
      location: props.location,
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <Suspense fallback={<BundleLoadingPlaceholder />}>
          <ContainerH360 {...props} />
        </Suspense>
      </HomieContext.Provider>
    );
  };

  renderAdvisorRegister = props => {
    const homieContext = {
      history: props.history,
      location: props.location,
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <Suspense fallback={<BundleLoadingPlaceholder />}>
          <ContainerAdvisor {...props} />
        </Suspense>
      </HomieContext.Provider>
    );
  };

  renderHomieLite = props => {
    const homieContext = {
      history: props.history,
      location: props.location,
      currentUser: this.state.currentUser,
      hideLoadingProcessView: this.hideLoadingProcessView,
      showLoadingProcessView: this.showLoadingProcessView
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <PermissionsProvider>
          <AuthGate allowed={[Admin]}>
            <Suspense fallback={<BundleLoadingPlaceholder />}>
              <HomieLiteDashboard {...props} />
            </Suspense>
          </AuthGate>
        </PermissionsProvider>
      </HomieContext.Provider>
    );
  };

  renderPageError = props => {
    const homieContext = {
      history: props.history,
      location: props.location,
      currentUser: this.state.currentUser
    };
    return (
      <HomieContext.Provider value={homieContext}>
        <Suspense fallback={<BundleLoadingPlaceholder />}>
          <PageError {...props} />
        </Suspense>
      </HomieContext.Provider>
    );
  };

  render() {
    const { currentUser } = this.state;

    return currentUser ? (
      <Provider store={reduxStore}>
        <IntlProvider
          locale={this.state.language}
          messages={texts[this.state.language]}
        >
          <Wrapper id="wrapper_content">
            <LoadingProcessHomie show={this.state.loadingProcessVisible} />
            <Helmet>
              <title>{meta.title}</title>
              <meta name="description" content={meta.description} />
              <meta name="keywords" content={meta.keywords} />
              <meta name="robots" content="index, follow" />
            </Helmet>
            <Router basename={'/h/'}>
              <Switch>
                <Route
                  path="/adelanta-tus-rentas"
                  render={() => this.renderAdelantoHomie()}
                />
                <Route
                  exact
                  path="/agencia/h360/inmuebles/:home_id/detalles"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  exact
                  path="/agencia/h360/inmuebles/:home_id/detalles"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  exact
                  path="/agencia/h360/inmuebles/:home_id/detalles"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  exact
                  path="/agencia/h360/inmuebles/:id/vista-previa"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  path="/agencia/citas/h360"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  path="/agencia/inmuebles/h360"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  path="/agencia/h360/publicar"
                  render={() => this.renderMainRoutes(true)}
                />
                <Route
                  path="/:form/:agency/h360/:reset_password_token?"
                  render={props => this.renderH360Register(props)}
                />
                <Route
                  path="/:advisor/h360/:form/:reset_password_token?"
                  render={props => this.renderH360Register(props)}
                />
                <Route
                  path={'/admin/homie-lite'}
                  component={this.renderHomieLite}
                />
                <Route
                  path="/admin"
                  render={() => this.renderMainRoutes(false)}
                />

                <Route
                  path="/inquilinos-externos"
                  render={props => this.renderExternalTenantRoutes(props)}
                />
                <Route
                  path="/dashboard/customer-success"
                  component={this.renderCustomerSuccessRoutes}
                />
                <Route
                  path="/dashboard/aplicaciones"
                  component={this.renderApplicationsDashboardRoutes}
                />
                <Route
                  path="/dashboard/permisos"
                  component={this.renderPermissionDashboardRoutes}
                />
                <Route
                  path="/reporte/aplicacion/:id"
                  render={props => this.renderReportDashboardRoutes(props)}
                />
                <Route
                  path="/:form/agencia/:slug"
                  render={props => this.renderAdvisorRegister(props)}
                />

                <Route
                  path="/renta-facil/error"
                  component={props => this.renderPageError(props)}
                />

                <Route path="/" render={() => this.renderMainRoutes(true)} />
              </Switch>
            </Router>
          </Wrapper>
        </IntlProvider>
      </Provider>
    ) : null;
  }
}
